import React, { useState, useEffect, useCallback } from 'react';
import api from '../services/api';
import { toast } from 'react-toastify';
import config from '../config/appConfig';
import '../styles/AssetManagement.css';

const AssetManagement = () => {
  const [assets, setAssets] = useState([]);
  const [stats, setStats] = useState(null);
  const [activeTab, setActiveTab] = useState('generate');
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({
    status: '',
    batchNumber: '',
    assetType: ''
  });

  const [generateForm, setGenerateForm] = useState({
    count: 10,
    itemType: 'card',
    batchNumber: ''
  });

  const loadStats = useCallback(async () => {
    try {
      const res = await api.get('/api/assets/stats');
      setStats(res.data.stats);
    } catch (error) {
      console.error('Failed to load stats');
    }
  }, []);

  const loadAssets = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      if (filters.status) params.append('status', filters.status);
      if (filters.batchNumber) params.append('batchNumber', filters.batchNumber);
      if (filters.assetType) params.append('assetType', filters.assetType);
      
      const res = await api.get(`/api/assets?${params.toString()}`);
      setAssets(res.data.assets);
    } catch (error) {
      toast.error('Failed to load assets');
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadStats();
    if (activeTab === 'inventory') {
      loadAssets();
    }
  }, [activeTab, loadStats, loadAssets]);

  const handleGenerateBatch = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const res = await api.post('/api/assets/generate-batch', generateForm);
      toast.success(`Generated ${res.data.assets.length} QR codes!`);
      
      // Download QR codes
      downloadQRCodes(res.data.assets, res.data.batchNumber);
      
      loadStats();
      setGenerateForm({ count: 10, itemType: 'card', batchNumber: '' });
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to generate batch');
    } finally {
      setLoading(false);
    }
  };

  const downloadQRCodes = (assets, batchNumber) => {
    // Create HTML for printable QR codes
    const htmlContent = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>QR Codes - ${batchNumber}</title>
        <style>
          @page { size: A4; margin: 10mm; }
          body { font-family: Arial, sans-serif; }
          .qr-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 20px; }
          .qr-card { text-align: center; page-break-inside: avoid; border: 1px solid #ddd; padding: 15px; }
          .qr-card img { width: 150px; height: 150px; }
          .asset-id { font-weight: bold; margin-top: 10px; font-size: 14px; }
          h1 { text-align: center; }
        </style>
      </head>
      <body>
        <h1>Medical QR Code Assets - ${batchNumber}</h1>
        <div class="qr-grid">
          ${assets.map(asset => `
            <div class="qr-card">
              <img src="${asset.qrCode}" alt="${asset.assetId}" />
              <div class="asset-id">${asset.assetId}</div>
              <div>${asset.qrCodeUrl}</div>
            </div>
          `).join('')}
        </div>
      </body>
      </html>
    `;

    const blob = new Blob([htmlContent], { type: 'text/html' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `QR-Codes-${batchNumber}.html`;
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadSingleQR = (asset) => {
    const link = document.createElement('a');
    link.href = asset.qrCode;
    link.download = `${asset.assetId}.png`;
    link.click();
  };

  return (
    <div className="asset-management">
      <div className="asset-header">
        <h1>QR Code Asset Management</h1>
        <p>Generate and manage physical QR code assets</p>
      </div>

      {stats && (
        <div className="stats-grid">
          <div className="stat-card">
            <h3>Total Assets</h3>
            <p className="stat-number">{stats.total}</p>
          </div>
          <div className="stat-card">
            <h3>Unregistered</h3>
            <p className="stat-number">{stats.unregistered}</p>
          </div>
          <div className="stat-card">
            <h3>Registered</h3>
            <p className="stat-number">{stats.registered}</p>
          </div>
          <div className="stat-card">
            <h3>Registration Rate</h3>
            <p className="stat-number">{stats.registrationRate}%</p>
          </div>
        </div>
      )}

      <div className="asset-tabs">
        <button 
          className={activeTab === 'generate' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('generate')}
        >
          Generate QR Codes
        </button>
        <button 
          className={activeTab === 'inventory' ? 'tab active' : 'tab'}
          onClick={() => setActiveTab('inventory')}
        >
          Inventory
        </button>
      </div>

      {activeTab === 'generate' && (
        <div className="tab-content">
          <h2>Generate New Batch</h2>
          
          <form onSubmit={handleGenerateBatch} className="generate-form">
            <div className="form-group">
              <label>Quantity (1-1000)</label>
              <input
                type="number"
                min="1"
                max="1000"
                value={generateForm.count}
                onChange={(e) => setGenerateForm({...generateForm, count: parseInt(e.target.value)})}
                required
              />
            </div>

            <div className="form-group">
              <label>Asset Type</label>
              <select
                value={generateForm.itemType}
                onChange={(e) => setGenerateForm({...generateForm, itemType: e.target.value})}
              >
                <option value="card">Card</option>
                <option value="bracelet">Bracelet</option>
                <option value="keychain">Keychain</option>
                <option value="sticker">Sticker</option>
                <option value="tag">Tag</option>
                <option value="other">Other</option>
              </select>
            </div>

            <div className="form-group">
              <label>Batch Number (optional)</label>
              <input
                type="text"
                placeholder="Leave empty for auto-generated"
                value={generateForm.batchNumber}
                onChange={(e) => setGenerateForm({...generateForm, batchNumber: e.target.value})}
              />
            </div>

            <button type="submit" className="btn btn-primary" disabled={loading}>
              {loading ? 'Generating...' : 'Generate Batch'}
            </button>
          </form>

          <div className="info-box">
            <h3>📦 What happens after generation?</h3>
            <ul>
              <li>QR codes are saved to the database</li>
              <li>Printable HTML file is automatically downloaded</li>
              <li>Each QR code has a unique ID (e.g., {config.assetPrefix}-A1B2C3D4)</li>
              <li>Assets can be registered by users via mobile or web</li>
            </ul>
          </div>
        </div>
      )}

      {activeTab === 'inventory' && (
        <div className="tab-content">
          <h2>Asset Inventory</h2>

          <div className="filters">
            <select
              value={filters.status}
              onChange={(e) => setFilters({...filters, status: e.target.value})}
            >
              <option value="">All Status</option>
              <option value="unregistered">Unregistered</option>
              <option value="registered">Registered</option>
              <option value="deactivated">Deactivated</option>
            </select>

            <select
              value={filters.assetType}
              onChange={(e) => setFilters({...filters, assetType: e.target.value})}
            >
              <option value="">All Types</option>
              <option value="card">Card</option>
              <option value="bracelet">Bracelet</option>
              <option value="keychain">Keychain</option>
              <option value="sticker">Sticker</option>
              <option value="tag">Tag</option>
              <option value="other">Other</option>
            </select>

            <input
              type="text"
              placeholder="Search batch number..."
              value={filters.batchNumber}
              onChange={(e) => setFilters({...filters, batchNumber: e.target.value})}
            />
          </div>

          {loading ? (
            <div className="loading">Loading...</div>
          ) : (
            <div className="assets-table">
              <table>
                <thead>
                  <tr>
                    <th>Asset ID</th>
                    <th>Type</th>
                    <th>Status</th>
                    <th>Batch</th>
                    <th>Registered To</th>
                    <th>Registration Date</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {assets.map((asset) => (
                    <tr key={asset._id}>
                      <td><code>{asset.assetId}</code></td>
                      <td>{asset.assetType}</td>
                      <td>
                        <span className={`status-badge ${asset.status}`}>
                          {asset.status}
                        </span>
                      </td>
                      <td>{asset.batchNumber}</td>
                      <td>
                        {asset.userId ? 
                          `${asset.userId.firstName} ${asset.userId.lastName}` : 
                          '-'
                        }
                      </td>
                      <td>
                        {asset.registrationDate ? 
                          new Date(asset.registrationDate).toLocaleDateString() : 
                          '-'
                        }
                      </td>
                      <td>
                        <button
                          onClick={() => downloadSingleQR(asset)}
                          className="btn btn-sm btn-secondary"
                        >
                          Download QR
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {assets.length === 0 && (
                <p className="no-data">No assets found with current filters</p>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AssetManagement;
